import React, { useState, useRef, useEffect } from 'react';
import { Brain, Monitor, Zap, Shield, Settings, Headphones } from 'lucide-react';
import { featuresData } from '../data/mock';

const iconMap = {
  Brain: Brain,
  Monitor: Monitor,
  Zap: Zap,
  Shield: Shield,
  Settings: Settings,
  HeadphonesIcon: Headphones,
};

export const Features = () => {
  const [hoveredFeature, setHoveredFeature] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="features" ref={sectionRef} className="py-20 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px opacity-20" style={{
          background: `linear-gradient(90deg, transparent, var(--accent-primary), transparent)`
        }}></div>
        <div className="absolute bottom-0 left-0 right-0 h-px opacity-20" style={{
          background: `linear-gradient(90deg, transparent, var(--accent-primary), transparent)`
        }}></div>
        
        {/* Floating Elements */}
        <div className="absolute top-40 left-20 w-4 h-4 rounded-full animate-float opacity-60" style={{
          background: 'var(--accent-primary)',
          animationDelay: '2s'
        }}></div>
        <div className="absolute bottom-40 right-32 w-6 h-6 rounded-full animate-float opacity-40" style={{
          background: 'var(--accent-purple)',
          animationDelay: '4s'
        }}></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full mb-6" style={{
            background: 'var(--accent-bg)',
            border: '1px solid var(--accent-primary)'
          }}>
            <Zap className="w-4 h-4" style={{ color: 'var(--accent-primary)' }} />
            <span className="body-sm font-medium" style={{ color: 'var(--accent-primary)' }}>
              Platform Features
            </span>
          </div>
          <h2 className="display-md mb-6" style={{ color: 'var(--text-primary)' }}>
            Why Choose Zany Automation?
          </h2>
          <p className="body-lg max-w-3xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
            Our platform combines cutting-edge technology with intuitive design to deliver 
            automation solutions that are both powerful and easy to use.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {featuresData.map((feature, index) => {
            const IconComponent = iconMap[feature.icon];
            const isHovered = hoveredFeature === index;
            
            return (
              <div
                key={feature.id}
                className={`group relative transition-all duration-1000 delay-${index * 100} ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                onMouseEnter={() => setHoveredFeature(index)}
                onMouseLeave={() => setHoveredFeature(null)}
              >
                {/* Feature Card */}
                <div className={`feature-card h-full transition-all duration-300 ${
                  isHovered ? 'scale-105' : ''
                }`}>
                  {/* Animated Background Gradient */}
                  <div className={`absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-10 transition-opacity duration-500`}
                    style={{
                      background: `linear-gradient(135deg, var(--accent-primary), var(--accent-purple))`
                    }}
                  ></div>

                  {/* Content */}
                  <div className="relative z-10">
                    {/* Icon */}
                    <div className={`w-16 h-16 rounded-xl flex items-center justify-center mb-6 transition-all duration-300 ${
                      isHovered ? 'animate-pulse-glow scale-110' : ''
                    }`} style={{
                      background: isHovered ? 'var(--accent-primary)' : 'var(--bg-tertiary)'
                    }}>
                      <IconComponent 
                        className={`w-8 h-8 transition-colors duration-300`}
                        style={{ 
                          color: isHovered ? 'var(--bg-primary)' : 'var(--accent-primary)' 
                        }} 
                      />
                    </div>

                    {/* Title */}
                    <h3 className="h3 mb-4 group-hover:text-white transition-colors duration-300" 
                        style={{ color: 'var(--text-primary)' }}>
                      {feature.title}
                    </h3>

                    {/* Description */}
                    <p className="body-md group-hover:text-gray-200 transition-colors duration-300" 
                       style={{ color: 'var(--text-secondary)' }}>
                      {feature.description}
                    </p>

                    {/* Hover Effect Lines */}
                    <div className={`absolute bottom-0 left-0 right-0 h-1 rounded-b-2xl transition-all duration-300 ${
                      isHovered ? 'opacity-100' : 'opacity-0'
                    }`} style={{
                      background: `linear-gradient(90deg, var(--accent-primary), var(--accent-purple))`
                    }}></div>
                  </div>
                </div>

                {/* Floating Connector Lines */}
                {index < featuresData.length - 1 && (
                  <div className="hidden lg:block absolute -right-4 top-1/2 transform -translate-y-1/2">
                    <div className={`w-8 h-px transition-all duration-500 ${
                      isHovered || hoveredFeature === index + 1 ? 'opacity-60' : 'opacity-20'
                    }`} style={{
                      background: 'var(--accent-primary)'
                    }}></div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Interactive Demo Section */}
        <div className={`mt-20 text-center transition-all duration-1000 delay-600 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="max-w-4xl mx-auto p-8 rounded-2xl relative overflow-hidden" style={{
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-subtle)'
          }}>
            {/* Animated Background */}
            <div className="absolute inset-0 opacity-5">
              <div className="absolute top-0 left-0 w-32 h-32 rounded-full animate-float" style={{
                background: `radial-gradient(circle, var(--accent-primary), transparent)`,
                animationDelay: '1s'
              }}></div>
              <div className="absolute top-0 right-0 w-24 h-24 rounded-full animate-float" style={{
                background: `radial-gradient(circle, var(--accent-purple), transparent)`,
                animationDelay: '3s'
              }}></div>
              <div className="absolute bottom-0 left-1/2 w-28 h-28 rounded-full animate-float" style={{
                background: `radial-gradient(circle, var(--accent-primary), transparent)`,
                animationDelay: '2s'
              }}></div>
            </div>

            <div className="relative z-10">
              <h3 className="h2 mb-4" style={{ color: 'var(--text-primary)' }}>
                Experience the Power of Automation
              </h3>
              <p className="body-lg mb-8" style={{ color: 'var(--text-secondary)' }}>
                See how our platform can transform your business processes with a personalized demo 
                tailored to your specific industry and use cases.
              </p>
              
              {/* Demo Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="p-4 rounded-xl" style={{ background: 'var(--bg-tertiary)' }}>
                  <div className="h3 mb-2" style={{ color: 'var(--accent-primary)' }}>15 min</div>
                  <div className="body-sm" style={{ color: 'var(--text-muted)' }}>Setup Time</div>
                </div>
                <div className="p-4 rounded-xl" style={{ background: 'var(--bg-tertiary)' }}>
                  <div className="h3 mb-2" style={{ color: 'var(--accent-primary)' }}>90%</div>
                  <div className="body-sm" style={{ color: 'var(--text-muted)' }}>Time Saved</div>
                </div>
                <div className="p-4 rounded-xl" style={{ background: 'var(--bg-tertiary)' }}>
                  <div className="h3 mb-2" style={{ color: 'var(--accent-primary)' }}>0</div>
                  <div className="body-sm" style={{ color: 'var(--text-muted)' }}>Manual Errors</div>
                </div>
              </div>

              <button 
                className="btn-primary animate-pulse-glow"
                onClick={() => {
                  const element = document.querySelector('#contact');
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
              >
                Request Live Demo
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};